import React, { useCallback, useEffect, useState, useMemo } from 'react';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import { useResponsiveContext } from '../../context/ResponsiveContext';
import Onboarding from '../../components/Onboarding';
import { isChrome } from '../../../common/utils/browser-detector';
import MobileHeader from '../../components/MobileHeader';
import { useUserContext } from '../../context/UserContext';
import DashboardContent from '../../features/Dashboard/components/DashboardContent/DashboardContent';
import LocalStorageService from '../../service/LocalStorageService';
import { useHistory } from 'react-router-dom';

const viewModeToken = { GRID_VIEW: 'GRID_VIEW', LIST_VIEW: 'LIST_VIEW' };
const viewModeInit = viewModeToken.GRID_VIEW;

export default function Dashboard() {
  const { user, userLoading, appConfig } = useUserContext();
  const [viewMode, setViewMode] = useState(viewModeInit);
  const [searchModal, setSearchModal] = useState(false);
  const isListView = useMemo(() => viewMode === viewModeToken.LIST_VIEW, [viewMode]);

  const handleSetListView = useCallback(() => {
    if (!isListView) {
      setViewMode(viewModeToken.LIST_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.LIST_VIEW);
    }
  }, [isListView]);

  const handleSetGridView = useCallback(() => {
    if (isListView) {
      setViewMode(viewModeToken.GRID_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.GRID_VIEW);
    }
  }, [isListView]);

  const { isMdAndBelow } = useResponsiveContext();

  const [isWebActivated, setIsWebActivated] = useState(undefined);
  const [isOnboardingShowing, setIsOnboardingShowing] = useState(false);

  const history = useHistory();

  useEffect(() => {
    // Check app config
    if (!appConfig) {
      return;
    }

    let appConfigFeatures = appConfig.features;
    if (appConfigFeatures) {
      const featureWebAccess = appConfigFeatures.find(feature => feature.name === 'web_access');
      if (featureWebAccess) {
        if (!featureWebAccess.enabled) {
          history.push('/logout', { web_access_error: true });
          return;
        }
      }
    }
  }, [appConfig]);

  useEffect(() => {
    const savedViewMode = LocalStorageService.getItem('ViewMode');

    if (savedViewMode) {
      setViewMode(savedViewMode);
    }

    const extensionId = process.env.EXT_ID;
    window.postMessage({ type: extensionId, message: 'LOGIN_FROM_APP' }, '*');

    const isiOS =
      !navigator.userAgent.match('CriOS') &&
      (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !window.MSStream;

    isiOS && window.addEventListener('popstate', () => window.location.reload());
  }, []);

  const closeSearchModal = () => {
    setSearchModal(false);
  };

  const openSearchModal = () => {
    setSearchModal(true);
  };

  useEffect(() => {
    if (user !== null) {
      const isEmptyFeature = user.userStats?.find(event => event.key === 'has_recorded_web_video');
      if (isEmptyFeature) {
        setIsWebActivated(Boolean(isEmptyFeature.value));
      } else {
        setIsWebActivated(false);
      }

      setIsOnboardingShowing(!Boolean(user?.onboarded));
    }
  }, [user]);

  if (user === null || userLoading) {
    return null;
  }

  // TODO: onboarding show!!!
  // Comment it for testing
  if (isChrome && isOnboardingShowing && !userLoading) {
    return <Onboarding setIsOnboardingShowing={setIsOnboardingShowing} />;
  }

  return (
    <>
      <ProtectedPageSidebar />
      <MobileHeader
        libraryMode={true}
        userAvatar={user?.avatarUri}
        userFirstLetter={user?.userFullName.charAt(0).toUpperCase()}
        openSearchModal={openSearchModal}
      />
      <DashboardContent
        userLoading={userLoading}
        isWebActivated={isWebActivated}
        user={user}
        isMdAndBelow={isMdAndBelow}
        handleSetGridView={handleSetGridView}
        handleSetListView={handleSetListView}
        isListView={isListView}
        searchModal={searchModal}
        closeSearchModal={closeSearchModal}
      />
    </>
  );
}
